import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _regeneratorRuntime from "@babel/runtime/regenerator";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import { apiCallPromise } from './apiCallPromise';
import { TIME_OUT, TTL, getCacheData, isCachedDataStale } from './cache';
import { logGQLErrors } from './logGQLErrors';
import { getDefaultNavMenu } from './navigationMenu';

/**
 * fetchNavigationMenu is used to fetch NavigationMenu Data from LPC BFF repo
 * @param navigationDataType - id of the component for which the data to be fetched.
 * @param locale - User preferred locale based on browser language preference
 * @param useCache - Flag to bypass cache
 * @param clientEnv - Client environment : dev | test | stage | prod
 * @param previewMode - Flag to fetch preview data from contentful.
 * @param accessToken - contentful Access Token to access contentful preview data.
 * @param evictCache - evict In-Memory Cache
 * @param ttl - time to live value of In-Memory cache. Default value is 30 minutes.
 * @param timeOut - API call time out. Default value is 150 milli seconds
 * @param oneGraphEndpoint - Directly pass oneGraph endpoint for non .com domain requests to avoid CORS errors. eg: https://gateway.chegg.com.mx/one-graph/graphql 
 * @returns { NavigationMenuResponse } Returns a response of type `NavigationMenuResponse`
 * @example <caption>`fetchNavigationMenu` is used to fetch NavigationMenu Data of Footer/Header</caption>
 * import { fetchNavigationMenu } from '@chegg-me-components/navigation-data';
 * import { SignedOutHeader } from '@chegg-me-components/signed-out-header';
 * import { SignedOutFooter } from '@chegg-me-components/signed-out-footer';
 *
 * const App = async () => {
 *  const { SignedOutHeader, SignedOutFooter } = await fetchNaviationData({
 *    navigationDataType: NavigationDataType.SignedOutNavigation,
 *    clientEnv: 'test',
 *  })
 *  return (
 *    <>
 *      <SignedOutHeader
 *        navLinks={SignedOutHeader.PrimaryNav},
 *        secondaryNavLinks={SignedOutHeader.SecondaryNav}
 *      />
 *      <RestOfTheApp />
 *      <SignedOutFooter
 *        navLinks={SignedOutFooter.PrimaryNav},
 *        legalLinks={SingedOutFooter.LegalNav}
 *      />
 *    </>
 *  )
 * }
 */
export var fetchNavigationMenu = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(_ref) {
    var navigationDataType, locale, clientEnv, _ref$useCache, useCache, _ref$isPreviewMode, isPreviewMode, accessToken, _ref$evictCache, evictCache, _ref$ttl, ttl, _ref$timeOut, timeOut, countryCode, _ref$device, device, oneGraphEndpoint, parseNavigationMenu, cacheKey, isStale, _getCacheData, _getCacheData$navigat, _navigationMenu, timeoutPromise, promiseArray, navigationMenu, _ref6, _navigationMenu2;

    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            navigationDataType = _ref.navigationDataType, locale = _ref.locale, clientEnv = _ref.clientEnv, _ref$useCache = _ref.useCache, useCache = _ref$useCache === void 0 ? true : _ref$useCache, _ref$isPreviewMode = _ref.isPreviewMode, isPreviewMode = _ref$isPreviewMode === void 0 ? false : _ref$isPreviewMode, accessToken = _ref.accessToken, _ref$evictCache = _ref.evictCache, evictCache = _ref$evictCache === void 0 ? false : _ref$evictCache, _ref$ttl = _ref.ttl, ttl = _ref$ttl === void 0 ? TTL : _ref$ttl, _ref$timeOut = _ref.timeOut, timeOut = _ref$timeOut === void 0 ? TIME_OUT : _ref$timeOut, countryCode = _ref.countryCode, _ref$device = _ref.device, device = _ref$device === void 0 ? 'desktop' : _ref$device, oneGraphEndpoint = _ref.oneGraphEndpoint;

            parseNavigationMenu = function parseNavigationMenu(navigationMenu) {
              if (!navigationDataType.includes(',')) return _defineProperty({}, navigationDataType, _objectSpread({}, navigationMenu));
              return _objectSpread({}, navigationMenu);
            };

            cacheKey = "".concat(navigationDataType).concat(locale ? "/".concat(locale) : '').concat(countryCode ? "/".concat(countryCode) : '');
            _context.prev = 3;
            isStale = isCachedDataStale(cacheKey, ttl);
            /** returns cached data when isPreviewMode is false
             *  and useCache is true and evictCache is false
             *  and cached data is available and is not stale*/

            if (!(!isStale && !isPreviewMode && useCache && !evictCache)) {
              _context.next = 8;
              break;
            }

            _getCacheData = getCacheData(cacheKey), _getCacheData$navigat = _getCacheData.navigationMenu, _navigationMenu = _getCacheData$navigat === void 0 ? {} : _getCacheData$navigat;
            return _context.abrupt("return", _navigationMenu);

          case 8:
            timeoutPromise = function timeoutPromise(_ref4) {
              var timeOut = _ref4.timeOut,
                  cacheKey = _ref4.cacheKey,
                  navigationDataType = _ref4.navigationDataType,
                  locale = _ref4.locale;
              return new Promise(function (resolve) {
                setTimeout(function () {
                  /**Returns cached data if available, if not then returns default data*/
                  var _ref5 = getCacheData(cacheKey) || getDefaultNavMenu({
                    navigationDataType: navigationDataType,
                    locale: locale,
                    countryCode: countryCode
                  }),
                      navigationMenu = _ref5.navigationMenu;

                  resolve({
                    navigationMenu: navigationMenu,
                    hasTimedOut: true
                  });
                }, timeOut);
              });
            };

            promiseArray = [];
            /**set time out only when cached data is stale or cacheKey is not available*/

            if (isStale && useCache && !isPreviewMode && !evictCache) promiseArray.push(timeoutPromise({
              timeOut: timeOut,
              cacheKey: cacheKey,
              navigationDataType: navigationDataType,
              locale: locale
            }));
            navigationMenu = {};
            _context.next = 14;
            return Promise.race([apiCallPromise({
              navigationDataType: navigationDataType,
              useCache: useCache,
              evictCache: evictCache,
              clientEnv: clientEnv,
              accessToken: accessToken,
              isPreviewMode: isPreviewMode,
              locale: locale,
              countryCode: countryCode,
              device: device,
              isStale: isStale,
              cacheKey: cacheKey,
              oneGraphEndpoint: oneGraphEndpoint
            })].concat(promiseArray)).then(function (result) {
              navigationMenu = result.navigationMenu;

              if (result.hasTimedOut) {
                console.error("".concat(cacheKey, " API call Time Limit Exceeded - ").concat(timeOut, "ms"));
              }
            })["catch"](function (error) {
              throw error;
            });

          case 14:
            return _context.abrupt("return", navigationMenu);

          case 17:
            _context.prev = 17;
            _context.t0 = _context["catch"](3);
            logGQLErrors("Navigation Data fetching error - ".concat(cacheKey), _context.t0);
            /**In case of any error return cached data if available if not then returns default data*/

            _ref6 = getCacheData(cacheKey) || {}, _navigationMenu2 = _ref6.navigationMenu;
            /**
             * Parsing the getDefaultNavMenu so that in the respone, each data object has its componentId as a key.
             * @example
             * without parsing - { PrimaryNav, SecondaryNav }
             * with parsing - {SignedOutHeader: { PrimaryNav, SecondaryNav }}
             *  */

            return _context.abrupt("return", _navigationMenu2 || parseNavigationMenu(getDefaultNavMenu({
              navigationDataType: navigationDataType,
              locale: locale,
              countryCode: countryCode
            }).navigationMenu));

          case 22:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[3, 17]]);
  }));

  return function fetchNavigationMenu(_x) {
    return _ref2.apply(this, arguments);
  };
}();